import React, { ReactNode, useRef } from 'react';
import classNames from 'classnames';
import { Tooltip as RadixTooltip } from 'radix-ui';

import useMedia from '../../useMedia/useMedia';
import {
  ARROW_EDGE_PADDING_PX,
  ARROW_HEIGHT_PX,
  ARROW_WIDTH_PX,
  COLLISION_PADDING_PX,
  DEFAULT_DELAY_HIDE_MS,
  DEFAULT_DELAY_SHOW_MS,
  TOUCH_QUERY,
} from '../../floating/consts';
import parsePlacement from '../../floating/parsePlacement';
import type { FloatingPlacement } from '../../floating/FloatingPlacement';
import FloatingArrow from '../../floating/FloatingArrow';
import useCloseWhenHidden from '../../floating/useCloseWhenHidden';
import useTooltipState from '../model/useTooltipState';
import useTouchDismiss from '../../floating/useTouchDismiss';
import styles from './Tooltip.module.css';

export type TooltipProps = {
  /** Текст подсказки. Интерактивное содержимое (кнопки, ссылки) не поддерживается — для него нужен Popover. */
  content: ReactNode;
  /** Триггер. По умолчанию оборачивается в кнопку; с `asChild` подсказка вешается на переданный элемент. Для иконки без текста передайте `asChild` и элемент с `aria-label`. */
  children: ReactNode;
  /** Сторона и выравнивание: `top | bottom | left | right` с суффиксом `-start`/`-end`. `auto`, `auto-start`, `auto-end` выбирают сторону с наибольшим свободным местом. */
  placement?: FloatingPlacement;
  /** Подсказка не показывается. */
  isDisabled?: boolean;
  /** На устройствах без hover тап по триггеру открывает подсказку, повторный закрывает. Для триггера с собственным действием передайте `false`. */
  isTapEnabled?: boolean;
  /** Повесить подсказку на переданный элемент вместо кнопки-обёртки. Для отключённого контрола нужна обёртка `<span tabIndex={0}>`. */
  asChild?: boolean;
  /** Задержка показа по наведению и фокусу, мс. */
  delayShow?: number;
  /** Задержка скрытия после ухода курсора, мс. */
  delayHide?: number;
  /** Расстояние от триггера до подсказки, px: при `0` остриё стрелки касается триггера. */
  sideOffset?: number;
  /** Класс для тела подсказки, например для тени. */
  className?: string;
  /** Класс для стрелки. */
  arrowClassName?: string;
  /** Класс для кнопки-триггера. */
  triggerClassName?: string;
  /** Контейнер портала, по умолчанию `document.body`. */
  portalContainer?: HTMLElement | null;
};

/** Подсказка с текстом на примитиве Radix Tooltip: наведение, фокус, тап на touch-устройствах, авто-размещение. */
const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'auto',
  isDisabled = false,
  isTapEnabled = true,
  asChild = false,
  delayShow = DEFAULT_DELAY_SHOW_MS,
  delayHide = DEFAULT_DELAY_HIDE_MS,
  sideOffset = 0,
  className,
  arrowClassName,
  triggerClassName,
  portalContainer,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isTouchMedia = useMedia(TOUCH_QUERY);
  const parsedPlacement = parsePlacement(placement);

  const {
    isOpen,
    isTouch,
    side,
    alignOffset,
    close,
    handlePointerType,
    handleFocus,
    handleOpenChange,
    handleTriggerClick,
  } = useTooltipState({
    isDisabled,
    isTouchMedia,
    isTapEnabled,
    delayHide,
    placement: parsedPlacement,
    triggerRef,
  });

  useCloseWhenHidden({ isActive: isOpen, triggerRef, onHidden: close });
  useTouchDismiss({ isActive: isTouch && isOpen, triggerRef, contentRef, onDismiss: close });

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        delayDuration={delayShow}>
        <RadixTooltip.Trigger
          ref={triggerRef}
          asChild={asChild}
          type={asChild ? undefined : 'button'}
          className={classNames(!asChild && styles.trigger, triggerClassName)}
          onPointerEnter={handlePointerType}
          onPointerDown={handlePointerType}
          onFocus={handleFocus}
          onClick={handleTriggerClick}>
          {children}
        </RadixTooltip.Trigger>

        <RadixTooltip.Portal container={portalContainer}>
          <RadixTooltip.Content
            ref={contentRef}
            side={side}
            align={parsedPlacement.align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            collisionPadding={COLLISION_PADDING_PX}
            arrowPadding={ARROW_EDGE_PADDING_PX}
            hideWhenDetached
            onEscapeKeyDown={close}
            className={classNames(styles.content, className)}>
            {content}

            <RadixTooltip.Arrow
              width={ARROW_WIDTH_PX}
              height={ARROW_HEIGHT_PX}
              className={classNames(styles.arrow, arrowClassName)}
              asChild>
              <FloatingArrow />
            </RadixTooltip.Arrow>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

export default Tooltip;
